import qrcode from 'qrcode-generator';
import { solvePow } from '../../../packages/core/src/pow.ts';

export { solvePow };

/**
 * <pageid-stream customer="projekt-y" page="https://y.dk/…" server="https://pageid.app" size="320">
 * <pageid-stream customer="projekt-y" uid="…" version="1" server="https://pageid.app">
 *
 * Plays a PageID stream: an animated, fountain-coded QR code that the PageID
 * scanner verifies on its own. `<netid-stream>` (the product's first name) is
 * the same element and keeps working on every page that embeds it. With `page`, a live code for that page
 * (`/stream/<customer>/page?url=…`); with `uid`, an article stream
 * (`/stream/<customer>/<uid>`). Frames come from the PageID server as
 * Server-Sent Events; this element only draws them. `issuer` is the old
 * name of `customer`.
 *
 * Lifecycle: nothing is fetched when the script loads or the element is
 * created. The stream opens in connectedCallback and closes (with its frame
 * timer) in disconnectedCallback; while the document is hidden it is paused.
 *
 * Sessions (relay protection, layer 2; SPEC.md §7.2): every page view first
 * asks the PageID server for its own stream session (`POST /stream/<customer>/
 * session`), solving a small proof of work when the server asks for one, and
 * then streams it (`?session=`). Hiding or leaving the page ends the session; a
 * revoked session (its code was being shown to several phones: a copy) is
 * replaced by a new one after the back-off the server names, and the element
 * says "A copy of this code was blocked" for a moment.
 * Events: `netid-live` on the first packet, `netid-error` when the server
 * refuses the stream (the EventSource gives up), with the reason if known;
 * each is also sent as `pageid-live` / `pageid-error`.
 */
export class NetIdStream extends HTMLElement {
  static observedAttributes = ['customer', 'issuer', 'uid', 'page', 'version', 'server', 'size', 'fps'];

  private canvas: HTMLCanvasElement;
  private card: HTMLElement;
  private status: HTMLElement;
  private es: EventSource | null = null;
  /** Bumped on every start/stop: an older start's async steps give up. */
  private gen = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private failures = 0;
  /** The current session id (null while none). */
  session: string | null = null;
  /** Sessions this element has used (tests). */
  sessions = 0;
  private pending: string | null = null;
  private raf = 0;
  private onVisibility = () => {
    if (document.visibilityState === 'hidden') this.stop();
    else if (this.isConnected && !this.es) void this.start();
  };
  frames = 0;
  lastText = '';

  constructor() {
    super();
    const root = this.attachShadow({ mode: 'open' });
    root.innerHTML = `
      <style>
        :host { --netid-size: 240px; --u: 1; display: inline-block; vertical-align: top; font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif; line-height: 1.3; color: #0b1f17; }
        .card { position: relative; display: inline-block; padding: 1.5px; border-radius: calc(18px * var(--u)); overflow: hidden; isolation: isolate;
          box-shadow: 0 0 calc(26px * var(--u)) -6px rgba(16, 185, 129, .45), 0 10px 28px -14px rgba(11, 31, 23, .35); }
        .card::before { content: ''; position: absolute; z-index: -1; left: 50%; top: 50%; width: 200%; aspect-ratio: 1; transform: translate(-50%, -50%);
          background: conic-gradient(from 0deg, #047857, #10b981, #6ee7b7 30%, #047857 50%, #10b981 70%, #a7f3d0 82%, #047857);
          animation: netid-spin 9s linear infinite; }
        .inner { position: relative; display: flex; flex-direction: column; align-items: center; gap: calc(7px * var(--u));
          padding: calc(9px * var(--u)) calc(9px * var(--u)) calc(8px * var(--u)); border-radius: calc(16.5px * var(--u));
          background: linear-gradient(180deg, #ffffff 0%, #f3f8f5 100%); }
        canvas { display: block; image-rendering: pixelated; background: #fff; border-radius: calc(10px * var(--u)); width: var(--netid-size); height: var(--netid-size); }
        .label { display: flex; align-items: center; gap: calc(6px * var(--u)); font-size: max(11px, calc(12.5px * var(--u))); font-weight: 500; white-space: nowrap; color: #3d4a44; }
        .label b { font-family: 'Inter Tight', 'Inter', system-ui, sans-serif; font-weight: 700; letter-spacing: -.02em; color: #0b1f17; margin-left: 0.15em; }
        .label b i { font-style: normal; color: #047857; }
        .status { display: flex; align-items: center; gap: 5px; min-height: 1.3em; margin-top: calc(-4px * var(--u)); font-size: max(9.5px, calc(10.5px * var(--u))); color: #56625c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: var(--netid-size); }
        .dot { flex: none; width: 6px; height: 6px; border-radius: 50%; background: #a3aea8; }
        .live .dot { background: #10b981; box-shadow: 0 0 6px #10b981; animation: netid-pulse 1.6s ease-in-out infinite; }
        @keyframes netid-spin { to { transform: translate(-50%, -50%) rotate(360deg); } }
        @keyframes netid-pulse { 50% { opacity: .4; } }
        .card.idle::before, .idle .live .dot { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .card::before, .live .dot { animation: none; } }
      </style>
      <div class="card idle" part="card">
        <div class="inner">
          <canvas part="canvas" role="img" aria-label="PageID code: scan it with the PageID scanner to see who runs this page"></canvas>
          <div class="label" part="label">
            <span>Scan with <b>Page<i>ID</i></b></span>
          </div>
          <div class="status" part="status" role="status"><span class="dot"></span><span class="text">connecting…</span></div>
        </div>
      </div>`;
    this.canvas = root.querySelector('canvas')!;
    this.card = root.querySelector('.card')!;
    this.status = root.querySelector('.status')!;
    this.applySize();
  }

  /** Tile width in CSS px, clamped to 120–480 (designed for 160–320). */
  private applySize() {
    const s = Math.min(480, Math.max(120, this.size));
    // On the shadow card, not the host: a custom element's constructor may not add attributes.
    const card = this.card;
    card.style.setProperty('--netid-size', `${s}px`);
    card.style.setProperty('--u', String(Math.min(1.25, Math.max(0.72, s / 240))));
  }

  connectedCallback() {
    document.addEventListener('visibilitychange', this.onVisibility);
    if (document.visibilityState !== 'hidden') void this.start();
    else this.setStatus('paused');
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.stop();
  }

  /** True while an EventSource is open (or connecting). */
  get streaming(): boolean {
    return this.es !== null;
  }

  attributeChangedCallback(n: string, oldV: string | null, newV: string | null) {
    if (n === 'size') this.applySize();
    if (oldV !== newV && this.isConnected && n !== 'size' && document.visibilityState !== 'hidden') void this.start();
  }

  get size(): number {
    return Number(this.getAttribute('size') ?? 320) || 320;
  }

  /** Starts (again): a new session, then its stream. */
  private async start() {
    this.stop();
    const gen = this.gen;
    const customer = this.getAttribute('customer') ?? this.getAttribute('issuer') ?? 'projekt-y';
    const uid = this.getAttribute('uid');
    const page = this.getAttribute('page');
    if (!uid && !page) return this.setStatus('no page (or story uid) given');
    // PageID moved from netid.dev-y.com to pageid.app (2026-10-26): an old embed's `server` is mapped, as the
    // session POST would not survive the old host's 301 (a 301 turns a POST into a GET without its body).
    const server = (this.getAttribute('server') ?? '').replace(/\/$/, '').replace(/^https:\/\/netid\.dev-y\.com$/, 'https://pageid.app');
    const sq = new URLSearchParams();
    if (uid) sq.set('uid', uid);
    if (this.getAttribute('version')) sq.set('version', this.getAttribute('version')!);
    if (page) sq.set(uid ? 'page' : 'url', page);
    this.card.classList.remove('idle');
    if (!this.notice) this.setStatus('connecting…');
    const got = await this.newSession(`${server}/stream/${encodeURIComponent(customer)}/session`, sq, gen);
    if (gen !== this.gen || !got) return;
    const q = new URLSearchParams({ session: got });
    for (const a of ['version', 'fps']) if (this.getAttribute(a)) q.set(a, this.getAttribute(a)!);
    const url = `${server}/stream/${encodeURIComponent(customer)}/${uid ? encodeURIComponent(uid) : 'page'}?${q}`;
    this.session = got;
    this.sessions++;
    const es = new EventSource(url);
    this.es = es;
    let live = false;
    es.addEventListener('packet', (e) => {
      const p = JSON.parse((e as MessageEvent).data) as { k: number; size: number };
      this.failures = 0;
      if (!this.notice) this.setStatus(`Live · ${p.k} pieces`, true);
      this.status.title = `signed packet: ${p.size} bytes in ${p.k} blocks`;
      if (!live) {
        live = true;
        this.emit('live', p);
      }
    });
    es.addEventListener('frame', (e) => {
      this.pending = (JSON.parse((e as MessageEvent).data) as { q: string }).q;
      if (!this.raf) this.raf = requestAnimationFrame(() => this.draw());
    });
    // The server revoked this session (its code was shown to several phones at once: a copy). A real
    // reader gets a new one after the back-off; the old code stops being signed, so copies go stale.
    es.addEventListener('revoked', (e) => {
      const d = JSON.parse((e as MessageEvent).data || '{}') as { reason?: string; retry_after?: number };
      this.emit('revoked', d);
      this.hint('A copy of this code was blocked');
      this.restartIn((d.retry_after ?? 0) * 1000, gen);
    });
    es.addEventListener('notice', () => this.hint('This code was scanned elsewhere'));
    // The same browser opened this page again (another tab): one tab, one session.
    es.addEventListener('replaced', () => {
      this.stop();
      this.setStatus('paused: this code is showing in another tab');
    });
    es.addEventListener('ended', () => this.restartIn(0, gen));
    es.onerror = () => {
      if (this.es !== es) return;
      // CLOSED: the server refused the stream (an HTTP error); EventSource will not retry.
      if (es.readyState !== 2) return this.setStatus('reconnecting…');
      this.es = null;
      if (live && this.failures < 3) {
        // The session ended on the server (a restart, its lifetime): take a new one.
        this.failures++;
        return this.restartIn(1000 * this.failures, gen);
      }
      this.setStatus('not available');
      void this.reason(url).then((reason) => {
        if (reason) this.setStatus(reason);
        this.emit('error', { reason });
      });
    };
  }

  private notice = false;
  private hint(text: string) {
    this.notice = true;
    this.setStatus(text, true);
    setTimeout(() => (this.notice = false), 8000);
  }

  private restartIn(ms: number, gen: number) {
    this.closeStream();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (ms > 0) this.setStatus(`renewing the code in ${Math.ceil(ms / 1000)} s…`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (gen === this.gen && this.isConnected && document.visibilityState !== 'hidden') void this.start();
    }, ms);
  }

  /** POST …/session: the session id, after a proof of work if the server asks for one (null = gave up, status set). */
  private async newSession(url: string, q: URLSearchParams, gen: number): Promise<string | null> {
    let device: string | null = null;
    try {
      device = localStorage.getItem('pageid-device');
    } catch {}
    for (let attempt = 0; attempt < 3; attempt++) {
      if (device) q.set('device', device);
      let r: Response;
      try {
        r = await fetch(`${url}?${q}`, { method: 'POST', credentials: 'include', cache: 'no-store' });
      } catch {
        this.setStatus('offline: retrying…');
        this.restartIn(5000, gen);
        return null;
      }
      const body = (await r.json().catch(() => ({}))) as { session?: string; device?: string; error?: string; retry_after?: number; pow?: { challenge: string; bits: number } };
      if (gen !== this.gen) return null;
      if (body.device) {
        device = body.device;
        try {
          localStorage.setItem('pageid-device', device);
        } catch {}
      }
      if (r.ok && body.session) return body.session;
      if (r.status === 428 && body.pow) {
        const nonce = await solvePow(body.pow.challenge, body.pow.bits, () => gen !== this.gen);
        if (nonce === null) return null;
        q.set('pow', `${body.pow.challenge}.${nonce}`);
        continue;
      }
      if (r.status === 429 || r.status === 503) {
        const wait = Math.max(5, body.retry_after ?? 30);
        this.setStatus(`paused · new code in ${wait} s`);
        this.restartIn(wait * 1000, gen);
        return null;
      }
      this.setStatus(body.error ?? `HTTP ${r.status}`);
      this.emit('error', { reason: body.error ?? null });
      return null;
    }
    return null;
  }

  /** Sends `netid-<name>` and its new name `pageid-<name>`. */
  private emit(name: string, detail: unknown) {
    this.dispatchEvent(new CustomEvent(`netid-${name}`, { detail }));
    this.dispatchEvent(new CustomEvent(`pageid-${name}`, { detail }));
  }

  /** The server's error text for a refused stream (it answers JSON `{ error }`). */
  private async reason(url: string): Promise<string | null> {
    try {
      const r = await fetch(url, { headers: { accept: 'application/json' } });
      if (r.ok) {
        await r.body?.cancel();
        return null;
      }
      return ((await r.json()) as { error?: string }).error ?? `HTTP ${r.status}`;
    } catch {
      return null;
    }
  }

  private closeStream() {
    this.es?.close();
    this.es = null;
    this.session = null;
  }

  private stop() {
    this.gen++;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.closeStream();
    this.card.classList.add('idle');
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private setStatus(text: string, live = false) {
    this.status.classList.toggle('live', live);
    this.status.querySelector('.text')!.textContent = text;
  }

  private draw() {
    this.raf = 0;
    const text = this.pending;
    if (!text) return;
    this.pending = null;
    const qr = qrcode(0, 'M');
    qr.addData(text, 'Alphanumeric');
    qr.make();
    const n = qr.getModuleCount();
    // Whole device pixels per module, so every module is crisp; whatever is
    // left over widens the white quiet zone (never below 4 modules).
    const quiet = 4;
    const dpr = Math.max(1, Math.min(3, globalThis.devicePixelRatio || 1));
    const css = Math.min(480, Math.max(120, this.size));
    const scale = Math.max(1, Math.floor((css * dpr) / (n + 2 * quiet)));
    const px = Math.max(Math.round(css * dpr), (n + 2 * quiet) * scale);
    if (this.canvas.width !== px || this.canvas.height !== px) this.canvas.width = this.canvas.height = px;
    const off = Math.floor((px - n * scale) / 2);
    const ctx = this.canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = '#0b1f17';
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) ctx.fillRect(off + c * scale, off + r * scale, scale, scale);
    this.frames++;
    this.lastText = text;
    this.dispatchEvent(new CustomEvent('netid-frame', { detail: { text, frames: this.frames } }));
  }
}

/** `<pageid-stream>`: the product name. A second class, because one constructor can back only one element name. */
export class PageIdStream extends NetIdStream {}

if (!customElements.get('netid-stream')) customElements.define('netid-stream', NetIdStream);
if (!customElements.get('pageid-stream')) customElements.define('pageid-stream', PageIdStream);
